import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Plus, 
  X, 
  Save, 
  Loader2, 
  AlertCircle,
  Tag,
  Hash
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface CargoIgreja {
  id: string
  igreja_id: string
  nome: string
  descricao: string | null
  nivel_hierarquia: number
  responsabilidades: string[] | null
  ativo: boolean | null
  criado_em: string | null
  atualizado_em: string | null
  criado_por: string | null
}

interface CargoFormDialogProps {
  trigger: React.ReactNode
  cargo?: CargoIgreja
  igrejaId: string
  onSave: (cargo: Omit<CargoIgreja, 'id' | 'criado_em' | 'atualizado_em' | 'criado_por'>) => Promise<void>
  isLoading?: boolean
}

export function CargoFormDialog({ 
  trigger, 
  cargo, 
  igrejaId, 
  onSave, 
  isLoading = false 
}: CargoFormDialogProps) {
  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useState<Omit<CargoIgreja, 'id' | 'criado_em' | 'atualizado_em' | 'criado_por'>>({
    igreja_id: igrejaId,
    nome: '',
    descricao: null,
    nivel_hierarquia: 5,
    responsabilidades: [],
    ativo: true
  })
  const [newResponsabilidade, setNewResponsabilidade] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // Resetar formulário quando abrir/fechar ou quando cargo mudar
  useEffect(() => {
    if (open) {
      if (cargo) {
        setFormData({
          igreja_id: cargo.igreja_id,
          nome: cargo.nome,
          descricao: cargo.descricao,
          nivel_hierarquia: cargo.nivel_hierarquia,
          responsabilidades: [...(cargo.responsabilidades ?? [])],
          ativo: cargo.ativo
        })
      } else {
        setFormData({
          igreja_id: igrejaId,
          nome: '',
          descricao: null,
          nivel_hierarquia: 5,
          responsabilidades: [],
          ativo: true
        })
      }
      setNewResponsabilidade('')
      setError(null)
    }
  }, [open, cargo, igrejaId])

  const handleInputChange = (field: keyof Omit<CargoIgreja, 'id'>, value: string | number | boolean | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const addResponsabilidade = () => {
    if (!newResponsabilidade.trim()) return
    
    const responsabilidade = newResponsabilidade.trim()
    const atuais = formData.responsabilidades ?? []
    if (atuais.includes(responsabilidade)) {
      setError('Esta responsabilidade já foi adicionada')
      return
    }

    setFormData(prev => ({
      ...prev,
      responsabilidades: [...(prev.responsabilidades ?? []), responsabilidade]
    }))
    setNewResponsabilidade('')
    setError(null)
  }

  const removeResponsabilidade = (index: number) => {
    setFormData(prev => ({
      ...prev,
      responsabilidades: (prev.responsabilidades ?? []).filter((_, i) => i !== index)
    }))
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addResponsabilidade()
    }
  }

  const handleSave = async () => {
    setError(null)
    
    // Validações
    if (!formData.nome.trim()) {
      setError('Nome do cargo é obrigatório')
      return
    }

    if (formData.nivel_hierarquia < 1 || formData.nivel_hierarquia > 10) {
      setError('Nível hierárquico deve estar entre 1 e 10')
      return
    }

    setIsSaving(true)

    try {
      await onSave({
        ...formData,
        nome: formData.nome.trim(),
        descricao: formData.descricao?.trim() || null
      })
      setOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar cargo')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {cargo ? 'Editar Cargo' : 'Novo Cargo'}
          </DialogTitle>
          <DialogDescription>
            {cargo 
              ? 'Edite as informações do cargo da igreja'
              : 'Crie um novo cargo para a estrutura organizacional da igreja'
            }
          </DialogDescription>
        </DialogHeader>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Nome do Cargo */}
          <div className="space-y-2">
            <Label htmlFor="nome">Nome do Cargo *</Label>
            <Input
              id="nome"
              value={formData.nome}
              onChange={(e) => handleInputChange('nome', e.target.value)}
              placeholder="Ex: Pastor, Diácono, Tesoureiro..."
              disabled={isSaving}
            />
          </div>

          {/* Nível Hierárquico */}
          <div className="space-y-2">
            <Label htmlFor="nivel">Nível Hierárquico *</Label>
            <div className="flex items-center gap-4">
              <Select
                value={formData.nivel_hierarquia.toString()}
                onValueChange={(value) => handleInputChange('nivel_hierarquia', parseInt(value))}
                disabled={isSaving}
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((nivel) => (
                    <SelectItem key={nivel} value={nivel.toString()}>
                      Nível {nivel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Hash className="h-4 w-4" />
                <span>1 = Maior autoridade, 10 = Menor autoridade</span>
              </div>
            </div>
          </div>

          {/* Descrição */}
          <div className="space-y-2">
            <Label htmlFor="descricao">Descrição</Label>
            <Textarea
              id="descricao"
              value={formData.descricao || ''}
              onChange={(e) => handleInputChange('descricao', e.target.value)}
              placeholder="Descreva as funções e atribuições deste cargo..."
              className="min-h-[80px]"
              disabled={isSaving}
            />
          </div>

          {/* Responsabilidades/Tags */}
          <div className="space-y-3">
            <Label>Responsabilidades/Tags</Label>
            
            {/* Input para adicionar nova responsabilidade */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Tag className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-10"
                  value={newResponsabilidade}
                  onChange={(e) => setNewResponsabilidade(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ex: Conferência de contas, Lançamento de dízimos..."
                  disabled={isSaving}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addResponsabilidade}
                disabled={!newResponsabilidade.trim() || isSaving}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Adicionar
              </Button>
            </div>

            {/* Lista de responsabilidades */}
            {(formData.responsabilidades?.length ?? 0) > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {(formData.responsabilidades?.length ?? 0)} responsabilidade{(formData.responsabilidades?.length ?? 0) !== 1
                    ? 's' : ''}:
                </p>
                <div className="flex flex-wrap gap-2">
                  {(formData.responsabilidades ?? []).map((resp, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="gap-2 pr-1"
                    >
                      {resp}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                        onClick={() => removeResponsabilidade(index)}
                        disabled={isSaving}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Botões de ação */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isSaving}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={isSaving || isLoading}
              className="gap-2"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isSaving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  )
}